import * as css from 'css/exports.module.scss';

import './UserChangesChart.scss';

import { getTimelineUserChangesSelector } from './@arch/selectors';
import { ModifiedByFilterParams } from './filters/ModifiedByFilter';
import { SalesforceUser } from '@ui/SalesforceUser';
import { SonarUser } from '@ui/SonarUser';

import { Column, ColumnConfig, Options, Plot } from '@ant-design/plots';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TimelineUserChange, UserWithMetadata } from '@sonar-software/sonar-core';
import { UpdateQueryParams, useSonarSelector, useUsers } from '@hooks';

const chartDataPadding = (length: number) => [ ...Array(Math.max(6 - length, 0)).keys() ].map((i) => ({
	changeCount: 0,
	salesforceUsername: `placeholder-username-${i}`
}));

const tooltipCustomContent = (_title: string, item: [{data: TimelineUserChange}]) =>
	(item[ 0 ]?.data.changeCount > 0 ?
		`<div class='chart-tooltip'>
			<div class='h3'>${item[ 0 ]?.data.salesforceName}</div>
			<div>${item[ 0 ]?.data.changeCount} changes</div>
		</div>` : null);

const chartConfig: ColumnConfig = {
	autoFit: true,
	columnWidthRatio: 0.9,
	data: [],
	height: 120,
	padding: 0,
	xAxis: {
		label: {
			formatter: () => null
		},
		tickLine: { length: 0 }
	},
	xField: 'salesforceUsername',
	yAxis: {
		label: {
			formatter: () => null
		}
	},
	yField: 'changeCount'
};

type PlotTooltip = { items: [{ data: TimelineUserChange}] };

type PlotEvent = { view: {
	controllers: [axis: unknown, legend: unknown, tooltip: PlotTooltip]
} };

export type UserChangesChartProps = {
	modifiedBy: ModifiedByFilterParams['modifiedByUsernames'];
	onChange: UpdateQueryParams<ModifiedByFilterParams>
};

export const UserChangesChart: React.FC<UserChangesChartProps> = ({ modifiedBy = [], onChange }) => {
	const users = useUsers();
	const userChanges = useSonarSelector(getTimelineUserChangesSelector.data);
	const [ selectedUser, setSelectedUser ] = useState('');

	useEffect(() => {
		if (selectedUser) {
			setSelectedUser('');
			const usernames = Array.isArray(modifiedBy) ? modifiedBy : [ modifiedBy ];

			const modifiedByUsernames = usernames.includes(selectedUser) ?
				usernames.filter((username) => username !== selectedUser) :
				[ ...usernames, selectedUser ];

			onChange({ modifiedByUsernames });
		}
	}, [ modifiedBy, onChange, selectedUser ]);

	const data = useMemo(() => [
		...userChanges ?? [],
		...chartDataPadding(userChanges?.length ?? 0)
	], [ userChanges ]);

	const userMap: Record<string, UserWithMetadata> = useMemo(
		() => users.reduce((map, user) => ({
			...map,
			[ user.email ]: user

		}), {}),
		[ users ]
	);

	const getColor = useCallback(({ salesforceUsername }: typeof data[0]) =>
		(!modifiedBy.length || modifiedBy.includes(salesforceUsername) ? css.addition : css.unmodified),
	[ modifiedBy ]);

	const onChartReady = useCallback((plot: Plot<Options>) => {
		plot.on('plot:click', (e: PlotEvent) => {
			const { changeCount, salesforceUsername } = e.view.controllers[ 2 ].items?.[ 0 ].data ?? {};

			if (changeCount) {
				setSelectedUser(salesforceUsername ?? '');
			}
		});
	}, []);

	if (!userChanges?.length) {
		return null;
	}

	const chartWidth = data.length * 28;

	return (
		<div
			className='user-changes-chart'
			style={ {
				maxWidth: chartWidth,
				minWidth: chartWidth
			} }>
			<Column
				className='chart'
				{ ...chartConfig }
				color={ getColor }
				data={ data }
				onReady={ onChartReady }
				tooltip={ { customContent: tooltipCustomContent } } />
			<div className='user-avatars'>
				{
					userChanges.map(
						({ salesforceName, salesforceUsername }) => {
							const userId = userMap[ salesforceUsername ]?.id;

							switch (true) {
							case Number.isInteger(userId):
								return (
									<SonarUser
										key={ salesforceUsername }
										shape='square'
										showTooltip
										userId={ userId }
										withFullName={ false } />
								);
							case Boolean(salesforceUsername):
								return (
									<SalesforceUser
										key={ salesforceUsername }
										name={ salesforceName }
										shape='square'
										showTooltip
										username={ salesforceUsername }
										withFullName={ false } />
								);
							default:
								return null;
							}
						}
					)
				}
			</div>
		</div>
	);
};
